import { useEffect, useState } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { questionsApi, QuestionGroup, Question } from '../services/api';

const SEVERITIES = ['low', 'medium', 'high', 'critical'];
const ROLES = ['SM', 'MM', 'DM', 'LP', 'BUYER', 'ELT', 'VM', 'IT'];
const ROLE_LABELS: Record<string, string> = {
  SM: 'Store Manager',
  MM: 'Market Manager',
  DM: 'District Manager',
  LP: 'Loss Prevention',
  BUYER: 'Buyer',
  ELT: 'Executive Leadership',
  VM: 'Visual Merchandising',
  IT: 'IT Team',
};

interface QuestionFormData {
  group_id: string;
  text: string;
  severity: string;
  description: string;
  role: string;
}

const emptyQuestionForm: QuestionFormData = {
  group_id: '',
  text: '',
  severity: 'medium',
  description: '',
  role: '',
};

interface GroupFormData {
  name: string;
}

const emptyGroupForm: GroupFormData = {
  name: '',
};

// Force rebuild v2
export default function Questions() {
  const [hierarchicalGroups, setHierarchicalGroups] = useState<QuestionGroup[]>([]);
  const [flatGroups, setFlatGroups] = useState<QuestionGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Question modal state
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [questionForm, setQuestionForm] = useState<QuestionFormData>(emptyQuestionForm);

  // Group modal state
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<QuestionGroup | null>(null);
  const [groupForm, setGroupForm] = useState<GroupFormData>(emptyGroupForm);

  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const groupsData = await questionsApi.listHierarchicalGroups();
      setHierarchicalGroups(groupsData);

      // Build flat list of all groups for the dropdown
      const flat: QuestionGroup[] = [];
      for (const group of groupsData) {
        if (group.children && group.children.length > 0) {
          // This is a parent group - add its children
          for (const child of group.children) {
            flat.push(child);
          }
        } else if (group.questions && group.questions.length > 0) {
          // This is a standalone group with questions
          flat.push(group);
        }
      }
      setFlatGroups(flat);

      // Expand all groups by default
      const allIds = new Set<string>();
      for (const group of groupsData) {
        allIds.add(group.id);
        if (group.children) {
          for (const child of group.children) {
            allIds.add(child.id);
          }
        }
      }
      setExpandedGroups(allIds);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }

  function toggleGroup(groupId: string) {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  }

  // Question handlers
  function openCreateQuestionModal(groupId?: string) {
    setEditingQuestion(null);
    setQuestionForm({ ...emptyQuestionForm, group_id: groupId || flatGroups[0]?.id || '' });
    setError(null);
    setShowQuestionModal(true);
  }

  function openEditQuestionModal(question: Question) {
    setEditingQuestion(question);
    setQuestionForm({
      group_id: question.group_id,
      text: question.text,
      severity: question.severity || 'medium',
      description: question.description || '',
      role: question.role || '',
    });
    setError(null);
    setShowQuestionModal(true);
  }

  async function handleQuestionSubmit(e: React.FormEvent) {
    e.preventDefault();

    // Validate required fields
    if (!questionForm.group_id) {
      setError('Please select a group');
      return;
    }
    if (!questionForm.text.trim()) {
      setError('Please enter question text');
      return;
    }

    setIsSaving(true);
    setError(null);

    console.log('Submitting question:', questionForm);

    try {
      if (editingQuestion) {
        await questionsApi.updateQuestion(editingQuestion.id, questionForm);
      } else {
        await questionsApi.createQuestion(questionForm);
      }
      await loadData();
      setShowQuestionModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save question');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDeleteQuestion(question: Question) {
    if (!confirm(`Are you sure you want to delete this question?`)) {
      return;
    }

    try {
      await questionsApi.deleteQuestion(question.id);
      await loadData();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete question');
    }
  }

  // Group handlers
  function openCreateGroupModal() {
    setEditingGroup(null);
    setGroupForm(emptyGroupForm);
    setError(null);
    setShowGroupModal(true);
  }

  function openEditGroupModal(group: QuestionGroup) {
    setEditingGroup(group);
    setGroupForm({
      name: group.name,
    });
    setError(null);
    setShowGroupModal(true);
  }

  async function handleGroupSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsSaving(true);
    setError(null);

    try {
      if (editingGroup) {
        await questionsApi.updateGroup(editingGroup.id, groupForm);
      } else {
        await questionsApi.createGroup(groupForm);
      }
      await loadData();
      setShowGroupModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save group');
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Questions</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage checklist questions and groups
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={openCreateGroupModal}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Group
          </button>
          <button
            onClick={() => openCreateQuestionModal()}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Question
          </button>
        </div>
      </div>

      {/* Question Groups - Hierarchical */}
      <div className="space-y-6">
        {hierarchicalGroups.map((parentGroup) => (
          <div key={parentGroup.id} className="bg-white shadow-sm rounded-xl overflow-hidden">
            {/* Parent Group Header */}
            <div
              className="flex items-center justify-between px-6 py-4 bg-primary-50 cursor-pointer border-b-2 border-primary-200"
              onClick={() => toggleGroup(parentGroup.id)}
            >
              <div className="flex items-center">
                {expandedGroups.has(parentGroup.id) ? (
                  <ChevronDownIcon className="h-6 w-6 text-primary-600 mr-3" />
                ) : (
                  <ChevronRightIcon className="h-6 w-6 text-primary-600 mr-3" />
                )}
                <div>
                  <h2 className="text-lg font-bold text-primary-900">
                    {parentGroup.name}
                  </h2>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-primary-700">
                  {parentGroup.totalQuestions || parentGroup.questions?.length || 0} questions
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openEditGroupModal(parentGroup);
                  }}
                  className="text-primary-600 hover:text-primary-900"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Child Groups or Direct Questions */}
            {expandedGroups.has(parentGroup.id) && (
              <div>
                {parentGroup.children && parentGroup.children.length > 0 ? (
                  // Render child groups
                  <div className="divide-y divide-gray-200">
                    {parentGroup.children.map((childGroup) => (
                      <div key={childGroup.id}>
                        {/* Child Group Header */}
                        <div
                          className="flex items-center justify-between px-6 py-3 bg-gray-50 cursor-pointer ml-4 border-l-4 border-primary-300"
                          onClick={() => toggleGroup(childGroup.id)}
                        >
                          <div className="flex items-center">
                            {expandedGroups.has(childGroup.id) ? (
                              <ChevronDownIcon className="h-5 w-5 text-gray-500 mr-3" />
                            ) : (
                              <ChevronRightIcon className="h-5 w-5 text-gray-500 mr-3" />
                            )}
                            <h3 className="text-sm font-semibold text-gray-800">
                              {childGroup.name}
                            </h3>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-500">
                              {childGroup.questions?.length || 0} questions
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditGroupModal(childGroup);
                              }}
                              className="text-primary-600 hover:text-primary-900"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>

                        {/* Child Group Questions */}
                        {expandedGroups.has(childGroup.id) && (
                          <div className="divide-y divide-gray-100 ml-4 border-l-4 border-gray-200">
                            {childGroup.questions?.map((question, index) => (
                              <div key={question.id} className="px-6 py-4 flex items-start justify-between hover:bg-gray-50">
                                <div className="flex items-start">
                                  <span className="text-sm text-gray-400 w-8">{index + 1}.</span>
                                  <div>
                                    <p className="text-sm text-gray-900">{question.text}</p>
                                    {question.description && (
                                      <p className="text-xs text-gray-500 mt-1">{question.description}</p>
                                    )}
                                    <div className="flex gap-2 mt-1">
                                      {question.role && (
                                        <span className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-700">
                                          {ROLE_LABELS[question.role] || question.role}
                                        </span>
                                      )}
                                      <span className={`px-2 py-0.5 text-xs rounded ${
                                        question.severity === 'critical' ? 'bg-red-100 text-red-700' :
                                        question.severity === 'high' ? 'bg-orange-100 text-orange-700' :
                                        question.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'
                                      }`}>
                                        {question.severity || 'medium'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => openEditQuestionModal(question)}
                                    className="text-primary-600 hover:text-primary-900"
                                  >
                                    <PencilIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteQuestion(question)}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                            {(!childGroup.questions || childGroup.questions.length === 0) && (
                              <div className="px-6 py-8 text-center text-gray-500 text-sm">
                                No questions in this group.{' '}
                                <button
                                  onClick={() => openCreateQuestionModal(childGroup.id)}
                                  className="text-primary-600 hover:underline"
                                >
                                  Add one
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  // Render direct questions (for groups without children)
                  <div className="divide-y divide-gray-100">
                    {parentGroup.questions?.map((question, index) => (
                      <div key={question.id} className="px-6 py-4 flex items-start justify-between hover:bg-gray-50">
                        <div className="flex items-start">
                          <span className="text-sm text-gray-400 w-8">{index + 1}.</span>
                          <div>
                            <p className="text-sm text-gray-900">{question.text}</p>
                            {question.description && (
                              <p className="text-xs text-gray-500 mt-1">{question.description}</p>
                            )}
                            <div className="flex gap-2 mt-1">
                              {question.role && (
                                <span className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-700">
                                  {ROLE_LABELS[question.role] || question.role}
                                </span>
                              )}
                              <span className={`px-2 py-0.5 text-xs rounded ${
                                question.severity === 'critical' ? 'bg-red-100 text-red-700' :
                                question.severity === 'high' ? 'bg-orange-100 text-orange-700' :
                                question.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'
                              }`}>
                                {question.severity || 'medium'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openEditQuestionModal(question)}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteQuestion(question)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                    {(!parentGroup.questions || parentGroup.questions.length === 0) && (
                      <div className="px-6 py-8 text-center text-gray-500 text-sm">
                        No questions in this group.{' '}
                        <button
                          onClick={() => openCreateQuestionModal(parentGroup.id)}
                          className="text-primary-600 hover:underline"
                        >
                          Add one
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Question Modal */}
      {showQuestionModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-25" onClick={() => setShowQuestionModal(false)} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {editingQuestion ? 'Edit Question' : 'Add Question'}
              </h2>

              {error && (
                <div className="mb-4 p-3 bg-red-50 rounded-lg text-sm text-red-700">
                  {error}
                </div>
              )}

              <form onSubmit={handleQuestionSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Group
                  </label>
                  <select
                    value={questionForm.group_id}
                    onChange={(e) => setQuestionForm({ ...questionForm, group_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  >
                    <option value="">Select a group</option>
                    {flatGroups.map((group) => (
                      <option key={group.id} value={group.id}>{group.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Question Text
                  </label>
                  <textarea
                    required
                    rows={2}
                    value={questionForm.text}
                    onChange={(e) => setQuestionForm({ ...questionForm, text: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description (optional)
                  </label>
                  <textarea
                    rows={2}
                    value={questionForm.description}
                    onChange={(e) => setQuestionForm({ ...questionForm, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Additional details about the question"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Applies To (Role)
                  </label>
                  <select
                    value={questionForm.role}
                    onChange={(e) => setQuestionForm({ ...questionForm, role: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">All Roles</option>
                    {ROLES.map((role) => (
                      <option key={role} value={role}>{ROLE_LABELS[role]}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Severity
                  </label>
                  <select
                    value={questionForm.severity}
                    onChange={(e) => setQuestionForm({ ...questionForm, severity: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    {SEVERITIES.map((severity) => (
                      <option key={severity} value={severity}>{severity.charAt(0).toUpperCase() + severity.slice(1)}</option>
                    ))}
                  </select>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowQuestionModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50"
                  >
                    {isSaving ? 'Saving...' : editingQuestion ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Group Modal */}
      {showGroupModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-25" onClick={() => setShowGroupModal(false)} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {editingGroup ? 'Edit Group' : 'Add Group'}
              </h2>

              {error && (
                <div className="mb-4 p-3 bg-red-50 rounded-lg text-sm text-red-700">
                  {error}
                </div>
              )}

              <form onSubmit={handleGroupSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Group Name
                  </label>
                  <input
                    type="text"
                    required
                    value={groupForm.name}
                    onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="e.g., Store Manager"
                  />
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowGroupModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50"
                  >
                    {isSaving ? 'Saving...' : editingGroup ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
